import type { CSSProperties } from 'react';
import { DatePicker, Input, Select } from 'antd';
import dayjs from 'dayjs';
import type { FieldDefinition } from '@lens/shared';
import { CURRENT_USER_TOKEN } from '@lens/shared';
import { dateFieldValue, DATE_FORMAT } from '../lib/dateValue';

export const OPERATOR_LABELS: Record<string, string> = {
  eq: '=',
  neq: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  contains: 'содержит',
  not_contains: 'не содержит',
  contains_any: 'содержит любой',
  contains_all: 'содержит все',
  in: 'в списке',
  not_in: 'не в списке',
  is_empty: 'пусто',
  is_not_empty: 'не пусто',
};

export function getOperatorsForFieldType(fieldType: string): string[] {
  switch (fieldType) {
    case 'text':
    case 'textarea':
    case 'markdown':
      return ['eq', 'neq', 'contains', 'not_contains', 'is_empty', 'is_not_empty'];
    case 'number':
    case 'computed':
      return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_empty', 'is_not_empty'];
    case 'select':
      return ['eq', 'neq', 'in', 'not_in', 'is_empty', 'is_not_empty'];
    case 'multi_select':
      return ['contains_any', 'contains_all', 'not_contains', 'is_empty', 'is_not_empty'];
    case 'reference':
    case 'user':
      return ['eq', 'neq', 'in', 'not_in', 'is_empty', 'is_not_empty'];
    case 'date':
      return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_empty', 'is_not_empty'];
    case 'boolean':
      return ['eq', 'neq', 'is_empty', 'is_not_empty'];
    case 'url':
      return ['is_empty', 'is_not_empty', 'contains'];
    default:
      return ['eq', 'neq', 'is_empty', 'is_not_empty'];
  }
}

export interface FilterValueInputProps {
  field: FieldDefinition | null;
  operator: string;
  value: unknown;
  onChange: (v: unknown) => void;
  refOptions?: Array<{ value: string; label: string }>;
  onRefFocus?: () => void;
  /** Узкие поля ввода — для компактного редактора условий. */
  compact?: boolean;
}

export function FilterValueInput({
  field,
  operator,
  value,
  onChange,
  refOptions = [],
  onRefFocus,
  compact,
}: FilterValueInputProps): JSX.Element {
  // В компактном режиме значение тянется по остатку строки, а не задаёт её ширину.
  const w = (base: string, min: number): CSSProperties =>
    compact ? { flex: '1 1 160px', minWidth: 120 } : { flex: base, minWidth: min };
  if (!field) {
    return (
      <Input
        size="small"
        style={w('0 1 220px', 160)}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Значение"
      />
    );
  }
  if (field.fieldType === 'boolean') {
    return (
      <Select
        size="small"
        style={w('0 1 120px', 100)}
        value={value === true ? 'true' : value === false ? 'false' : (value as string | undefined)}
        onChange={(v) => onChange(v === 'true')}
        options={[
          { value: 'true', label: 'Да' },
          { value: 'false', label: 'Нет' },
        ]}
        placeholder="Значение"
      />
    );
  }
  if (field.fieldType === 'select' || field.fieldType === 'multi_select') {
    // Опции select могут приходить в двух форматах: объекты `{ value, label }`
    // (поля типов записей) ИЛИ массив строк `["Back", "Front", …]` (select-колонки
    // справочников, в т.ч. через reference_attribute). Нормализуем оба, иначе для
    // строковых опций value/label = undefined и выпадашка фильтра пустая.
    const opts = (
      (field.config as { options?: Array<string | { value: string; label?: string }> })?.options ?? []
    ).map((o) => (typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label || o.value }));
    const isMulti = ['in', 'not_in', 'contains_any', 'contains_all'].includes(operator);
    return (
      <Select
        size="small"
        mode={isMulti ? 'multiple' : undefined}
        // В компактном режиме выбранные значения сворачиваются в «+N», иначе
        // чипы переносятся и условие перестаёт быть однострочным.
        maxTagCount={compact && isMulti ? 'responsive' : undefined}
        style={w('0 1 240px', 180)}
        value={isMulti ? (Array.isArray(value) ? value : []) : (value as string | undefined)}
        onChange={(v) => onChange(v)}
        options={opts}
        placeholder="Значение"
        showSearch
        filterOption={(input, opt) =>
          (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    );
  }
  if (field.fieldType === 'reference' || field.fieldType === 'user') {
    const isMulti = ['in', 'not_in'].includes(operator);
    // Первым пунктом — подстановка «я»: условие хранит токен, а не конкретного
    // человека, и на сервере раскрывается в того, кто смотрит. Так один общий
    // быстрый фильтр «Мои задачи» каждому показывает его собственные записи.
    const options = [
      { value: CURRENT_USER_TOKEN, label: 'Я (кто смотрит)' },
      ...refOptions.filter((o) => o.value !== CURRENT_USER_TOKEN),
    ];
    return (
      <Select
        size="small"
        mode={isMulti ? 'multiple' : undefined}
        maxTagCount={compact && isMulti ? 'responsive' : undefined}
        style={w('0 1 260px', 180)}
        value={isMulti ? (Array.isArray(value) ? value : []) : (value as string | undefined)}
        onChange={(v) => onChange(v)}
        options={options}
        placeholder="Значение"
        showSearch
        allowClear={!isMulti}
        onFocus={onRefFocus}
        filterOption={(input, opt) =>
          (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    );
  }
  if (field.fieldType === 'date') {
    return (
      <DatePicker
        size="small"
        format={DATE_FORMAT}
        style={w('0 1 180px', 160)}
        value={value ? dayjs(value as string) : null}
        onChange={(d) => onChange(dateFieldValue(d) ?? undefined)}
      />
    );
  }
  if (field.fieldType === 'number') {
    return (
      <Input
        size="small"
        style={w('0 1 140px', 120)}
        type="number"
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(e.target.value === '' ? undefined : Number.isNaN(n) ? e.target.value : n);
        }}
        placeholder="Значение"
      />
    );
  }
  return (
    <Input
      size="small"
      style={w('0 1 220px', 160)}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Значение"
    />
  );
}
